import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router'

import { useState } from 'react'
import { ActivityIndicator } from '../components'
import Data from '../components/aivy-admin/data'
import Logs from '../components/aivy-admin/logs'
import Feedback from '../components/feedback'
import SEO from '../components/seo'
import Button from '../components/tailwind/Button'
import PageHeading from '../components/tailwind/PageHeading'
import AssessmentMonitor from '../components/talent/AssessmentMonitor'
import CareersMatchingContainer from '../components/talent/careers-matching-container'
import PdfBundleDialog from '../components/talent/pdf-bundle-dialog'
import SpaceActionsDropdown, {
  LOCKED_TALENT_ACTIONS
} from '../components/talent/space-actions-dropdown'
import SpaceComment from '../components/talent/space-comment'
import SpaceCompletedChallenges from '../components/talent/space-completed-challenges'
import SpaceDetailsAssessment from '../components/talent/space-details-assessment'
import SpaceDetailsProfile from '../components/talent/space-details-profile'
import SpaceDocuments from '../components/talent/space-documents'
import SpaceFavored from '../components/talent/space-favored'
import SpaceHeader from '../components/talent/space-header'
import SpaceHistory from '../components/talent/space-history'
import SpaceRadarChart from '../components/talent/space-radar-chart'
import SpaceSetStateHint from '../components/talent/space-set-state-hint'
import SpaceTtlTimeline from '../components/talent/space-ttl-timeline'
import TalentLockedView, {
  TALENT_DETAIL_VIEWS,
  TalentResultsSkeleton,
  resolveTalentDetailView
} from '../components/talent/talent-locked-view'
import TalentSearchCombobox from '../components/talent/TalentSearchCombobox'
import trackEvents from '../constants/track-events'
import { useNotificationContext, useUserContext } from '../context'
import { useTalentLock } from '../features/pricing/hooks'
import { query } from '../graphql'
import { trackEvent } from '../helpers/analytics'
import {
  usePdfBundleExport,
  usePdfBundleOptions
} from '../hooks/use-pdf-bundle'
import { useSpaceActions } from '../hooks/use-space-actions'
import { useTalentQuery } from '../hooks/useTalentQuery'
import Error404 from './error-404'

import { QUERY_KEYS } from 'constants/constants'

const Talent = () => {
  const { id } = useParams() // CHECK SECURITY
  const { alert, error, success } = useNotificationContext()

  const {
    isAdmin,
    cognitoUser: { username }
  } = useUserContext()

  const [displayLogs, setDisplayLogs] = useState(false)
  const [displayData, setDisplayData] = useState(false)
  const [isResetting, setIsResetting] = useState(false)
  // Von wo der PDF-Dialog geoeffnet wurde (ACTIONS, ATTACHMENTS), sonst null.
  const [pdfBundleSource, setPdfBundleSource] = useState(null)

  const {
    status: spaceQueryStatus,
    data: space,
    refetch: refetchSpace
  } = useTalentQuery(id, username)

  // Talentspeicher (D9). Den Rang liefert der Cache der Talentliste, die
  // TalentSearchCombobox ohnehin lädt. Gesperrt wird nur in enforced. Bei
  // pending (vorläufig oder noch nicht gezählt) zeigt die Seite Platzhalter
  // statt Ergebnissen, ein Query-Fehler ergibt die volle Seite (fail-open).
  // Ergebnisse laden am owner, deshalb zählt auch die Sperre der Person.
  const lock = useTalentLock(id, { owner: space?.owner })
  const view = resolveTalentDetailView(lock)
  const resultsHidden = view !== TALENT_DETAIL_VIEWS.FULL

  // amplify bug! - updateSpace: res - { comment: null }
  // const spaceMutation = useMutationAndUpdateQuery({
  //   queryKey: ['space', id],
  //   mutation: 'updateSpace'
  // })

  const { career_id, assessment_id } = space || {}

  const {
    status: careerQueryStatus,
    fetchStatus: careerFetchStatus,
    data: career
  } = useQuery({
    queryKey: QUERY_KEYS.careerById(career_id),
    queryFn: () => query({ query: 'getCareer', variables: { id: career_id } }),
    enabled:
      !!assessment_id &&
      assessment_id !== 'TALENTPOOL' &&
      assessment_id !== 'ATTRACT'
  })

  const spaceActions = useSpaceActions({ spaces: space ? [space] : [] })

  // Ohne Space lädt der Hook keine Dokumente, das PDF bleibt aus
  const pdfBundleOptions = usePdfBundleOptions({
    space: resultsHidden ? null : space,
    career
  })
  const pdfBundleExport = usePdfBundleExport()

  const loading = [
    spaceQueryStatus,
    careerFetchStatus !== 'idle' && careerQueryStatus
  ].includes('pending')

  if (loading) {
    return (
      <div className='mt-32 flex items-center justify-center'>
        <ActivityIndicator />
      </div>
    )
  }

  if (!space) return <Error404 />

  const openPdfBundle = (source) => {
    setPdfBundleSource(source)
    trackEvent(trackEvents.OPEN_PDF_BUNDLE_DIALOG, {
      source,
      document_count: pdfBundleOptions.documents.filter((doc) => doc.bundleable)
        .length
    })
  }

  const resetMatchingAndIntegration = () => {
    setIsResetting(true)

    query({
      query: 'spaceControl',
      variables: { action: 'resetMatchingForSpace', user_id: space.owner }
    })
      .then(() => {
        success('Das Matching wurde aktualisiert!')
        refetchSpace()
      })
      .catch((err) => {
        error()
        console.error(err)
      })
      .finally(() => setIsResetting(false))
  }

  const resetMatchingAndIntegrationAlert = () => {
    alert(
      'Achtung',
      [
        'Das Matching wird für alle Talente von diesem Owner neu berechnet und mögliche Integrationen werden geupdatet.',
        'Möchtest du fortfahren?'
      ],
      [
        { onPress: resetMatchingAndIntegration, text: 'Ja' },
        { text: 'Abbrechen', style: 'cancel' }
      ]
    )
  }

  const adminPanel = isAdmin && (
    <div className='flex flex-wrap items-center gap-2 bg-gray-100 px-4 py-4 sm:justify-end sm:px-6 lg:px-8'>
      {space.progress === 100 && (
        <span className='mr-auto text-sm text-gray-600'>
          {/* Regelstand, mit dem der gespeicherte Score entstand. Ohne Stempel:
              Score von vor der Einführung der Regelstände (02.11.2026) */}
          Regelstand {space.original.matching_score_revision ?? 'ohne Stempel'}
        </span>
      )}
      {space.progress === 100 && (
        <Button.SecondaryLG
          text='Reset'
          onClick={resetMatchingAndIntegrationAlert}
          isLoading={isResetting}
        />
      )}
      <Button.SecondaryLG
        text='Show Space'
        onClick={() => setDisplayData(true)}
      />
      <Button.SecondaryLG
        text='Show Logs'
        className='mr-2'
        onClick={() => setDisplayLogs(true)}
      />
    </div>
  )

  const adminDialogs = (
    <>
      <Logs
        title='SPACE-LOGS'
        open={displayLogs}
        setOpen={setDisplayLogs}
        logs={space.original.logs}
        displayKey='status'
      />
      <Data
        title='SPACE'
        open={displayData}
        setOpen={setDisplayData}
        data={space.original}
      />
    </>
  )

  if (view === TALENT_DETAIL_VIEWS.LOCKED) {
    return (
      <>
        <SEO title={`Talent ${space.name || space.identification}`} />
        <TalentLockedView
          space={space}
          career={career}
          refetchSpace={refetchSpace}
          spaceActions={spaceActions}
          rank={lock.rank}
          limit={lock.limit}
          adminPanel={adminPanel || null}
        />
        {adminDialogs}
        <Feedback context='TALENT' />
      </>
    )
  }

  const pending = view === TALENT_DETAIL_VIEWS.PENDING

  return (
    <>
      <SEO title={`Talent ${space.name || space.identification}`} />
      <PageHeading
        withActions={
          <div className='ml-auto flex items-center justify-center gap-x-3'>
            <TalentSearchCombobox currentSpaceId={id} />
            <SpaceFavored space={space} />
            <SpaceActionsDropdown
              space={space}
              refetchSpace={refetchSpace}
              career={career}
              spaceActions={spaceActions}
              canExportPdfBundle={pdfBundleOptions.canExport}
              onOpenPdfBundle={() => openPdfBundle('ACTIONS')}
              restrictedActions={pending ? LOCKED_TALENT_ACTIONS : undefined}
            />
          </div>
        }
        sticky
      >
        <SpaceHeader space={space} />
      </PageHeading>
      {adminPanel}
      <div className='mb-8 mt-8 min-h-full px-4 sm:px-6 lg:px-8'>
        <div className='mx-auto mb-4 grid grid-cols-1 gap-4 xl:grid-flow-col-dense xl:grid-cols-3'>
          <div className='space-y-4 xl:col-span-2 xl:col-start-1'>
            {pending ? (
              <TalentResultsSkeleton panels={3} announce />
            ) : (
              <>
                {space.progress === 100 && (
                  <SpaceSetStateHint
                    space={space}
                    spaceActions={spaceActions}
                  />
                )}
                <SpaceDetailsProfile
                  space={space}
                  refetchSpace={refetchSpace}
                />
                <SpaceDetailsAssessment
                  space={space}
                  refetchSpace={refetchSpace}
                  career={career}
                  spaceActions={spaceActions}
                  canExportPdfBundle={pdfBundleOptions.canExport}
                  isPdfBundleLoading={pdfBundleExport.isLoadingFor(space.id)}
                  onOpenPdfBundle={() => openPdfBundle('ATTACHMENTS')}
                />
                <SpaceDocuments space={space} refetchSpace={refetchSpace} />
              </>
            )}
          </div>
          <div className='space-y-4 xl:col-span-1 xl:col-start-3'>
            {pending ? (
              <TalentResultsSkeleton panels={2} />
            ) : (
              <>
                <SpaceHistory space={space} spaceActions={spaceActions} />
                {space.progress === 100 && (
                  <SpaceRadarChart space={space} career={career} />
                )}
                <AssessmentMonitor space={space} />
              </>
            )}
          </div>
        </div>
        <div className='mx-auto space-y-4'>
          {!pending && (
            <>
              <SpaceCompletedChallenges space={space} career={career} />
              <div className='md:flex md:items-center md:justify-between'>
                <CareersMatchingContainer
                  space={space}
                  refetchSpace={refetchSpace}
                />
              </div>
            </>
          )}
          {!!space.cron_ttls.length && (
            <div className='grid grid-cols-1 gap-4 xl:grid-flow-col-dense xl:grid-cols-3'>
              <div className='space-y-4 xl:col-span-2 xl:col-start-1'>
                <SpaceTtlTimeline space={space} />
              </div>
            </div>
          )}
          <SpaceComment
            data={{ space, refetchSpace }}
            // amplify bug! - updateSpace: res - { comment: null }
            // updateHandler={({ input, onSuccess }) =>
            //   spaceMutation.mutate({ input }, { onSuccess })
            // }
          />
        </div>

        {adminDialogs}
        {/* key: Auswahl gilt pro Talent, beim Wechsel startet sie neu. */}
        {!pending && (
          <PdfBundleDialog
            key={space.id}
            open={!!pdfBundleSource}
            onClose={() => setPdfBundleSource(null)}
            source={pdfBundleSource || undefined}
            space={space}
            career={career}
            options={pdfBundleOptions}
            bundleExport={pdfBundleExport}
          />
        )}
      </div>
      <Feedback context='TALENT' />
    </>
  )
}

export default Talent
